/**
 * What counts as this trimester's hiring, and what is merely on the list.
 *
 * THE TARGET IS THE TRIMESTER PLUS THREE MONTHS (Theresa, Sep 2026). A round
 * does not stop at its own last month: a seat starting in January is recruited
 * for in October, so T3 is measured on every start date from September through
 * March. Anything later is real approved demand and belongs on the board — but
 * it is not what this trimester is judged on, so it opens collapsed rather than
 * pushing the roles that ARE being worked off the first screen.
 *
 * Three groups come out of it, and the split is deliberate rather than a filter:
 * nothing is ever dropped, it is only folded away.
 *
 *   live      — in the target window, still being worked or already landed
 *   later     — approved, starting after the window. Collapsed.
 *   cancelled — approved then dropped. Collapsed, and red.
 */
import type { BoardRole } from '../../../../types/taDemand';
import { isDraft } from '../../../../types/taDemand';

/** How far past its own last month a trimester's target reaches. */
export const LOOKAHEAD_MONTHS = 3;

const pad = (n: number) => String(n).padStart(2, '0');

/** `t3-2026` → `{ from: '2026-09', to: '2027-03' }`. */
export function targetWindow(period: string): { from: string; to: string } | null {
  const m = /^t([123])-(\d{4})$/i.exec(period.trim());
  if (!m) return null;
  const t = Number(m[1]);
  const year = Number(m[2]);
  const start = (t - 1) * 4 + 1;
  const endIndex = start + 3 + LOOKAHEAD_MONTHS;
  return {
    from: `${year}-${pad(start)}`,
    to: endIndex > 12 ? `${year + 1}-${pad(endIndex - 12)}` : `${year}-${pad(endIndex)}`,
  };
}

/**
 * Which columns the table draws.
 *
 * `year` is every month the board carries — the default, and what the table has
 * always shown. The three trimesters narrow it to one round.
 */
export type ViewScope = 'year' | 't1' | 't2' | 't3';

export const SCOPES: { value: ViewScope; label: string }[] = [
  { value: 'year', label: 'Full year' },
  { value: 't1', label: 'T1' },
  { value: 't2', label: 'T2' },
  { value: 't3', label: 'T3' },
];

/**
 * The months a scope shows, in the board's own year.
 *
 * ⚠️ A TRIMESTER SHOWS ITS HIRING WINDOW, not its four months — `targetWindow`,
 * the same span the target is judged on. T3 is Sep–Mar, because a January start
 * is recruited for in October and is this round's work. Cutting at December
 * would hide the roles the round is measured on AND leave their rows blank,
 * since a marker can only be drawn in a column that is on screen.
 */
export function scopeWindow(scope: ViewScope, period: string): { from: string; to: string } | null {
  if (scope === 'year') return null;
  const year = /-(\d{4})$/.exec(period.trim())?.[1];
  return year ? targetWindow(`${scope}-${year}`) : null;
}

/**
 * Where a role SITS: the truth if somebody started, else the current
 * expectation, else the original commitment — the same precedence the server
 * uses to place the row on the timeline.
 */
export const effectiveMonth = (r: BoardRole): string =>
  (isDraft(r) ? r.month : r.actualMonth || r.adjustedMonth || r.plannedMonth || r.month) || '';

/**
 * Starting after the window, and only worth folding away when nobody is working
 * it yet. A role whose start is far out but which is already HIRED is part of
 * the trimester's delivery — the work happened here — so it stays visible.
 */
export function startsLater(r: BoardRole, w: { from: string; to: string } | null): boolean {
  if (!w || isDraft(r)) return false;
  if (r.status === 'hired') return false;
  const m = effectiveMonth(r);
  return Boolean(m) && m > w.to;
}

export type HiringGroups = { live: BoardRole[]; later: BoardRole[] };

/** Split a unit or team's roles into what is being worked and what is not yet. */
export function splitRoles(roles: BoardRole[], w: { from: string; to: string } | null): HiringGroups {
  const live: BoardRole[] = [];
  const later: BoardRole[] = [];
  for (const r of roles) (startsLater(r, w) ? later : live).push(r);
  return { live, later };
}

/**
 * The font a role's title is set in.
 *
 * Theresa's rule, and it is a rule about ATTENTION rather than about status:
 * black is work in flight, grey is on the list but not being worked, red is
 * gone. A hired role reads black too — it was this trimester's work and its
 * outcome is the thing the table exists to show.
 *
 * `cancelled` is a PARAMETER and not a status because it is not one: the type
 * has four (`hired | open | hold | backlog`) and the board carries dropped roles
 * in an array of their own, so which list a row came from is what knows.
 */
export function titleTone(r: BoardRole, cancelled = false): string {
  if (cancelled) return 'text-rose-600';
  if (isDraft(r)) return 'text-gray-900';
  if (r.status === 'backlog' || r.status === 'hold') return 'text-gray-400';
  return 'text-gray-900';
}
